import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface SalesChannel {
    id: number;
    salesChannelCode: string;
    salesChannelDescription: string;
    country: Country | null;
    pricelists: Pricelist[];
}

export interface Country {
    id: number;
    name: string;
}

export interface Pricelist {
    id: number;
    pricelistCode: string;
    pricelistName: string;
}

export interface TenantConfig {
    assignedSalesChannelCodes: string[];
}

// API calls

export async function getSalesChannel(id: number): Promise<SalesChannel> {
    return rms2Fetch<SalesChannel>(`saleschannels/${id}`);
}

export async function getTenantConfig(): Promise<TenantConfig> {
    return rms2Fetch<TenantConfig>('saleschannels/tenantconfig');
}

export async function getCountries(): Promise<Country[]> {
    const response = await rms2Fetch<{ countries: { id: number; name: string }[] }>('countries');
    return response.countries.map(c => ({ id: c.id, name: c.name }));
}

export async function createSalesChannel(salesChannel: Partial<SalesChannel>): Promise<SalesChannel> {
    return rms2Fetch<SalesChannel>('saleschannels', {
        method: 'POST',
        body: JSON.stringify({
            salesChannelCode: salesChannel.salesChannelCode,
            salesChannelDescription: salesChannel.salesChannelDescription,
            countryId: salesChannel.country?.id || null,
            pricelistIds: salesChannel.pricelists?.map(p => p.id) ?? [],
        }),
    });
}

export async function updateSalesChannel(salesChannel: SalesChannel): Promise<SalesChannel> {
    return rms2Fetch<SalesChannel>(`saleschannels/${salesChannel.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            salesChannelDescription: salesChannel.salesChannelDescription,
            countryId: salesChannel.country?.id || null,
            pricelistIds: salesChannel.pricelists?.map(p => p.id) ?? [],
        }),
    });
}
